import { createContext, useContext } from "react";

import type { ResolvedDateTimeFormat } from "@/shared/datetime";

import type { TimelineDataResult } from "./TimelineDataStreamer";
import type { TimeWindow, WindowUpdate } from "./window";

/**
 * What `<Timeline>` shares with its sections. Internal to the module: a value
 * lives here only when two or more sections read it; anything one section
 * needs is a prop `<Timeline>` passes to that section. The one exception is
 * `zoomIn` / `zoomOut`: only the toolbar reads them, but they sit beside
 * `fitAll` so the window's actions have one home.
 */
export interface TimelineContextValue<T> {
  /** The span on screen. */
  timeWindow: TimeWindow;
  /** Earliest item start to "now"; the window is clamped inside it. */
  extent: TimeWindow;
  /** Replace the window, or compute it from the current one; clamped to the extent. Immediate — no animation. */
  setTimeWindow: (update: WindowUpdate) => void;
  /** Narrow the span around its center by one zoom step (the + key's). */
  zoomIn: () => void;
  /** Widen the span around its center by one zoom step (the − key's). */
  zoomOut: () => void;
  /** Show the whole history: the window becomes the extent. */
  fitAll: () => void;
  /** The items in the window, and whether they are all loaded. */
  items: TimelineDataResult<T>;
  /**
   * The one formatter every section uses for dates and times: the user's
   * preference, resolved in the zone the Local/UTC toggle picked. Sections
   * must read it here and never call `useDateTimeFormat` themselves, or the
   * toggle would silently miss them. Anything that needs the zone itself (a
   * raw `Intl` call, the axis calendar, the date picker) reads `format.zone`.
   */
  format: ResolvedDateTimeFormat;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a single context serves every T; consumers narrow through useTimelineContext<T>()
export const TimelineContext = createContext<TimelineContextValue<any> | null>(
  null,
);

export function useTimelineContext<T>(): TimelineContextValue<T> {
  const value = useContext(TimelineContext);
  if (value === null) {
    throw new Error("Timeline sections must be rendered inside <Timeline>");
  }
  return value as TimelineContextValue<T>;
}
